import { type ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToken } from "@packsize/authentication";
import { type HubConnection } from "@microsoft/signalr";

import { ProductionContext } from "@/contexts/production";
import { removeLocalStorage, writeLocalStorage } from "@/api/local-storage";
import { useSignalRHub } from "@/hooks/use-signalr-hub";
import { baseRoutes } from "@/api/base-routes";
import * as machineGroupApi from "@/api/machine-group-api";
import { aliasSort } from "@/utils/sort";
import { type MachineGroup } from "@/schemas/machine-group";

import { MachineGroupContext } from "./machine-group.context";

export const MACHINE_GROUP_KEY = "currentMachineGroup";

interface MachineGroupProviderProps {
	readonly children: ReactNode;
}

export const MachineGroupProvider = ({ children }: MachineGroupProviderProps) => {
	const { bearerToken, decodedToken } = useToken();

	const { hubConnection, usePolling } = useSignalRHub({
		hubUrl: `${baseRoutes.machineGroupHub}/machineGroups`,
		token: bearerToken,
	});

	const { machineGroups, setMachineGroups, currentMachineGroup, setCurrentMachineGroup } =
		useContext(ProductionContext);

	const changeMachineGroup = useCallback(
		(mg: MachineGroup | null | undefined) => {
			setCurrentMachineGroup(mg || null);

			if (!mg) {
				removeLocalStorage(MACHINE_GROUP_KEY);
				console.info("Removed current machine group because we changed to undefined");
				return;
			}

			writeLocalStorage(MACHINE_GROUP_KEY, JSON.stringify(mg));
		},
		[setCurrentMachineGroup],
	);

	// Wiring up callbacks for when MachineGroups publishes messages
	useEffect(() => {
		if (!hubConnection || !decodedToken) return;

		const connection = hubConnection as HubConnection;

		connection.on("OnMachineGroupsVersion", (version: string) => {
			console.info(
				`signalr MachineGroup OnMachineGroupsVersion for connectionid ${connection.connectionId}- ${version}`,
			);
		});

		connection.on("OnMachineGroupUpdated", (tenantId: string, machineGroup: MachineGroup) => {
			if (tenantId !== decodedToken.tid) return;

			const oldMachineGroups = machineGroups.filter((pg: MachineGroup) => pg.id !== machineGroup.id);
			const newMachineGroups = [...oldMachineGroups, machineGroup].sort(aliasSort);
			setMachineGroups(newMachineGroups);

			if (currentMachineGroup && currentMachineGroup.id === machineGroup.id) changeMachineGroup(machineGroup);
		});

		connection.on("OnMachineGroupAdded", (tenantId: string, machineGroup: MachineGroup) => {
			if (tenantId !== decodedToken.tid) return;

			const newMachineGroups = [...machineGroups, machineGroup].sort(aliasSort);
			setMachineGroups(newMachineGroups);
		});

		connection.on("OnMachineGroupDeleted", (tenantId: string, deletedId: string) => {
			if (tenantId !== decodedToken.tid) return;

			const newMachineGroups = machineGroups.filter((mg: MachineGroup) => mg.id !== deletedId);
			setMachineGroups(newMachineGroups);

			if (currentMachineGroup?.id === deletedId) changeMachineGroup(null);
		});

		return () => {
			connection.off("OnMachineGroupsVersion");
			connection.off("OnMachineGroupUpdated");
			connection.off("OnMachineGroupAdded");
			connection.off("OnMachineGroupDeleted");
		};
	}, [hubConnection, currentMachineGroup, machineGroups, decodedToken, setMachineGroups, changeMachineGroup]);

	const queryFunction = async (): Promise<MachineGroup[]> => {
		const mgs = await machineGroupApi.getMachineGroups();

		const sortedMgs = mgs.sort(aliasSort);

		setMachineGroups(sortedMgs);
		if (!currentMachineGroup) {
			return sortedMgs;
		}

		const current = mgs.find((mg: MachineGroup) => mg.id === currentMachineGroup.id);
		if (!current) {
			changeMachineGroup(undefined);
		} else if (
			current.status !== currentMachineGroup.status ||
			current.productionEnabled !== currentMachineGroup.productionEnabled
		) {
			changeMachineGroup({ ...current });
		}
		return sortedMgs;
	};

	useQuery({
		queryKey: ["machineGroups"],
		queryFn: queryFunction,
		refetchInterval: usePolling ? 3000 : false,
		enabled: Boolean(bearerToken),
	});

	const value = useMemo(() => ({ changeMachineGroup }), [changeMachineGroup]);

	return <MachineGroupContext.Provider value={value}>{children}</MachineGroupContext.Provider>;
};
